import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, Subject, defer, from, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { AppService } from './app.service';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { SignalRService } from './signalr.service';
import { isPlayerStateNullState } from './player-state-error';
import { RequestsType } from '../model/appConfig';
import { ErrorCode } from '../model/errorcode';
import { State, toStateArray } from '../model/state.model';
import { Platform } from '@ionic/angular';
export class PlayerService {
    constructor(router, authService, apiService, appService, platform, alertController, signalRService) {
        this.router = router;
        this.authService = authService;
        this.apiService = apiService;
        this.appService = appService;
        this.platform = platform;
        this.alertController = alertController;
        this.signalRService = signalRService;
        this.interval = 0;
        this.ready = new BehaviorSubject(false);
        this.state = (new State()).init();
        this.prevTimeSong = 0;
        this.isPlay = false;
        this.commands = [];
        this.MAX_COMMAND_SEND = 10;
        this.playlistLoaded = new BehaviorSubject(false);
        /** Emitted when a state packet includes `playlists` (command 99 / full sync). Used to resolve SignalR playlist fetch. */
        this.playlistsArrived = new Subject();
        this.errCounter = 0;
        this.songChanged = new BehaviorSubject(false);
        this.noResults = false;
        this.deltaVolume = new BehaviorSubject(null);
        this.volumeChanged = new EventEmitter();
        this.platformPauseSubject = new BehaviorSubject(false);
        if (this.authService.authenticated) {
            this.start().catch(error => {
                console.error('Failed to start player service:', error);
            });
        }
        this.platformPauseSubject.subscribe((pauseValue) => {
            // on open app from background force to download all information in sync()
            if (pauseValue === false) {
                console.log('App resumed - resetting error counter and forcing sync');
                this.errCounter = 0; // Reset error counter on resume
                this.commands.push({ id: 99 });
            }
            else {
                console.log('App paused - maintaining background sync');
            }
        });
        this.signalRService.statesToProcess.subscribe((states) => {
            if (states !== null) {
                this.signalRService.statesToProcess.next(null);
                // "MyDjPlayerState_<id> is null" means the player has not reported any state yet
                // (offline / just started). The SignalR listener already drops such packets; the
                // HTTP (JSONP) transport used by the mobile builds delivers them here, and they
                // must not surface as an "Unknown error" popup. The server did answer, though,
                // so the connection is healthy - keep the error counter reset.
                const actionableStates = toStateArray(states).filter(state => !isPlayerStateNullState(state));
                this.errCounter = 0;
                if (actionableStates.length === 0) {
                    return;
                }
                actionableStates.forEach(state => { this.buildState(state); });
                this.ready.next(true);
            }
        });
    }
    start() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.errCounter = 0;
            const command = JSON.stringify({ "id": 99 });
            this.appService.showNotification("connecting...");
            try {
                const config = yield this.appService.getConfig();
                if (config.requestsType === RequestsType.SignalR) {
                    this.signalRService.sendMessage(this.authService.currentUser.name, command);
                }
                else {
                    this.apiService.sendCommand(this.authService.currentUser, command)
                        .subscribe(states => {
                        this.signalRService.statesToProcess.next(states);
                    });
                }
            }
            catch (error) {
                console.error('Failed to get config during start:', error);
                // Fallback to HTTP mode
                this.apiService.sendCommand(this.authService.currentUser, command)
                    .subscribe(states => {
                    this.signalRService.statesToProcess.next(states);
                });
            }
            this.interval = window.setInterval(() => {
                this.sync();
            }, 1000);
        });
    }
    stop() {
        clearInterval(this.interval);
        this.volumeChanged.emit({ volume: 0, force: true });
        this.state = (new State()).init();
        // Ensure the SignalR connection is closed so the next start connects with the current user
        try {
            this.signalRService.disconnect();
        }
        catch (err) {
            console.error('Error while disconnecting SignalR during stop:', err);
        }
    }
    sync() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                // 0 - получение минимума информации: текущего времени песни и некого кэш-тайма
                // 99 - получение максимума информации
                // если state еще не получен - дергаем сервер 99-й командой, если уже получен - то 0-й        
                let command = JSON.stringify({ id: this.state.currentSong.name === "" ? 99 : 0 });
                if (this.state.currentSong.name === "") {
                    this.appService.showNotification("connecting...");
                }
                // remove extra 99 comands
                let count = this.commands.filter(c => c.id === 99).length;
                if (count > 1) {
                    this.commands = this.commands.filter(c => c.id !== 99);
                    this.commands.push({ id: 99 });
                }
                if (this.commands.length > 0) {
                    if (this.commands.length > this.MAX_COMMAND_SEND) {
                        const firstCommands = this.commands.slice(0, this.MAX_COMMAND_SEND);
                        this.commands = this.commands.slice(this.MAX_COMMAND_SEND);
                        command = JSON.stringify(firstCommands);
                    }
                    else {
                        command = JSON.stringify(this.commands);
                        this.commands = [];
                    }
                }
                try {
                    const config = yield this.appService.getConfig();
                    if (config.requestsType === RequestsType.SignalR) {
                        this.signalRService.sendMessage(this.authService.currentUser.name, command);
                    }
                    else {
                        this.apiService.sendCommand(this.authService.currentUser, command)
                            .subscribe(states => {
                            this.signalRService.statesToProcess.next(states);
                        });
                    }
                }
                catch (error) {
                    console.error('Failed to get config during sync:', error);
                    // Fallback to HTTP mode
                    this.apiService.sendCommand(this.authService.currentUser, command)
                        .subscribe(states => {
                        this.signalRService.statesToProcess.next(states);
                    });
                }
            }
            catch (err) {
                console.error('Sync error:', err);
                this.appService.loading = "";
                // Only increment error counter if not in background
                if (!this.platformPauseSubject.value) {
                    this.errCounter++;
                    this.appService.showNotification("connection problem");
                }
                else {
                    console.log('Background sync error (not counting):', err);
                }
                // Increased threshold to prevent premature logout
                if (this.errCounter > 50) {
                    console.error('Too many errors, logging out');
                    this.stop();
                    this.authService.logout();
                    this.router.navigateByUrl("/auth/login");
                }
            }
        });
    }
    buildState(state) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            try {
                if (state.errorCode && !isPlayerStateNullState(state)) {
                    let errorHeader = "Error";
                    let errorMessage;
                    switch (state.errorCode) {
                        case ErrorCode.AuthFailed:
                            errorMessage = state.error;
                            break;
                        case ErrorCode.NoPermissionForCommand:
                            errorHeader = null;
                            errorMessage = `No permission for command ${state.error}`;
                            break;
                        default:
                            errorMessage = `Unknown error ${state.error}`;
                            break;
                    }
                    const alert = yield this.alertController.create({
                        header: errorHeader,
                        message: errorMessage,
                        buttons: ['OK'],
                    });
                    yield alert.present();
                }
                this.prevTimeSong = this.state.currentTimeSong;
                for (const field in state) {
                    if (field === "playlistSongs") {
                        this.state[field] = this.state[field].concat(state[field]);
                    }
                    else {
                        this.state[field] = state[field];
                    }
                    if (field === "volume") {
                        this.volumeChanged.emit({ volume: state[field] });
                    }
                }
                if (state.scheduler !== undefined) {
                    this.state.scheduler = state.scheduler;
                }
                if (state.playlists !== undefined && state.playlists !== null) {
                    this.playlistsArrived.next(state.playlists);
                }
                this.isPlay = this.state.currentTimeSong !== this.prevTimeSong;
                if (state.searchResultsList) { // switch off preloader when got search result         
                    this.appService.loading = "";
                    this.noResults = !state.searchResultsList.length;
                }
                if (state.playlistSongs && state.playlistSongs.length) { // switch off preloader when got playlist songs
                    this.appService.loading = "";
                    this.playlistLoaded.next(true);
                }
                // if currentSong changed then request full info to update current playlist
                if (state.currentSong && state.volume === undefined) {
                    this.commands.push({ id: 99 });
                    this.songChanged.next(true);
                }
            }
            catch (err) {
                console.error('Error in buildState:', err);
                // Don't crash, just log the error
            }
        });
    }
    /**
     * Loads playlists via the same command as full sync (id 99). HTTP: jsonp APIPhonePlayer; SignalR: hub message, then wait for playlists in state.
     */
    requestPlaylistsList(user) {
        const cmd = JSON.stringify({ id: 99 });
        const parsePlaylistsFromStates = (states) => {
            const withPlaylists = toStateArray(states).find(s => s && Array.isArray(s.playlists));
            const list = withPlaylists && Array.isArray(withPlaylists.playlists)
                ? withPlaylists.playlists
                : [];
            return list.length > 0 ? list : null;
        };
        return from(this.appService.getConfig()).pipe(switchMap(config => {
            if (config.requestsType === RequestsType.SignalR) {
                // Подписка на playlistsArrived должна быть активна до sendMessage, иначе быстрый ответ теряется.
                return defer(() => {
                    const stream = this.playlistsArrived.pipe(take(1), timeout(15000), catchError(() => of(null)));
                    queueMicrotask(() => this.signalRService.sendMessage(user.name, cmd));
                    return stream;
                });
            }
            return this.apiService.sendCommand(user, cmd).pipe(map(states => parsePlaylistsFromStates(states)), catchError(() => of(null)));
        }));
    }
    setVolume(v) {
        this.appService.showNotification("sending [volume]");
        let i = this.commands.findIndex(c => c.id === 8);
        if (i === -1) {
            this.commands.push({ id: 8, volume: v });
            this.commands.push({ id: 99 });
        }
        else {
            this.commands[i] = { id: 8, volume: v };
        }
    }
    play() {
        this.appService.showNotification("sending [play]");
        this.commands.push({ id: 1 });
    }
    pause() {
        this.appService.showNotification("sending [pause]");
        this.commands.push({ id: 2 });
    }
    nextTrack() {
        this.appService.showNotification("sending [next]");
        this.commands.push({ id: 3 });
        this.commands.push({ id: 99 });
    }
    happyBirthday() {
        this.appService.showNotification("sending [birthday]");
        this.commands.push({ id: 6 });
    }
    setShuffle(value) {
        this.appService.showNotification("sending [shuffle]");
        this.commands.push({ id: 7, shuffle: value ? "on" : "off" });
        this.commands.push({ id: 99 }); // после некоторых команд добавлена 99-я, чтобы принудительно получить статус от сервера       
    }
    seek(seekTime) {
        this.appService.showNotification("sending [seek]");
        this.commands.push({ id: 9, sec: seekTime });
    }
    search(s) {
        this.appService.showNotification("sending [search]");
        this.commands.push({ id: 14, searchString: s });
        this.commands.push({ id: 99 });
    }
    remove(songID) {
        this.appService.showNotification("sending [remove]");
        this.commands.push({ id: 15, songID: songID });
        this.commands.push({ id: 99 });
    }
    deleteForever(songID) {
        this.appService.showNotification("sending [delete forever]");
        this.commands.push({ id: 27, songID: songID });
        this.commands.push({ id: 99 });
    }
    playNext(songID) {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 16, songID: songID });
        this.commands.push({ id: 99 });
    }
    playNow(songID) {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 17, songID: songID });
        this.commands.push({ id: 99 });
    }
    setPlaylist(playlistID) {
        this.appService.showNotification("sending [set playlist]");
        this.commands.push({ id: 26, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }
    addPlaylist(playlistID) {
        this.appService.showNotification("sending [add playlist]");
        this.commands.push({ id: 35, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }
    getPlaylistSongs(playlistID) {
        this.appService.showNotification("sending [get playlist songs]");
        this.commands.push({ id: 13, playlistID: playlistID });
    }
    playlistSongPlayNow(playlistID, songID) {
        this.appService.showNotification("sending [playlist song - play now]");
        this.commands.push({ id: 22, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    }
    playlistSongPlayNext(playlistID, songID) {
        this.appService.showNotification("sending [playlist song - play next]");
        this.commands.push({ id: 21, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    }
    // в поисковом списке свои идентификаторы, поэтому для манипуляций с песнями из списка найденных существуют отдельные функции
    playNextAfterSearch(songID) {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 23, songID: songID });
        this.commands.push({ id: 99 });
    }
    playNowAfterSearch(songID) {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 24, songID: songID });
        this.commands.push({ id: 99 });
    }
    // additional Newyear functions
    ny1() {
        this.appService.showNotification("sending [ny1]");
        this.commands.push({ id: 28 });
        this.commands.push({ id: 99 });
    }
    ny2() {
        this.appService.showNotification("sending [ny2]");
        this.commands.push({ id: 29 });
        this.commands.push({ id: 99 });
    }
    UpdateLikeStatusOfPlaylist(playlistId, likeType) {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 33, "playlistID": playlistId, "likeType": likeType });
        this.commands.push({ id: 99 });
    }
    UpdateLikeStatusOfSong(page, playlistId, songId, typeLike) {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 32, page: page, playlistID: playlistId, songID: songId, likeType: typeLike });
        this.commands.push({ id: 99 });
    }
    AllHeartsNextInMainPlaylist() {
        this.appService.showNotification("sending All hearts Next in the main PL");
        this.commands.push({ id: 34 });
        this.commands.push({ id: 99 });
    }
    AllHeartsNextInSpecificPlaylist(playlistID) {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 30, playlistID: playlistID });
        this.commands.push({ id: 99 });
    }
    AllHeartsNextInTheSearchResults() {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 31 });
        this.commands.push({ id: 99 });
    }
}
